import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { keyBy, uniqueId } from '@src/utils/functions/utils'

import Product from '../../domains/Product'
import { ReturnLineItem } from '../../services/ordersAndReturnService/ordersAndReturn.types'
import productService from '../../services/productService'

import LineItem from './types/LineItem/LineItem'
import Order from './types/Order'
import RETURN_MODES from './types/ReturnModes'
import RETURN_TYPES from './types/ReturnTypes'
import { ProductReturnCard } from './types/useReturnController.types'

const getReturnableQuantity = (lineItem: LineItem) => (lineItem.quantity || 0) - (lineItem.returnedQuantity || 0)

const isLineItemEligibleForReturn = (lineItem: LineItem) => {
  const returnableQuantity = getReturnableQuantity(lineItem)
  return lineItem.isLineItemReturnable && returnableQuantity > 0
}

const makeProductReturnCard = (lineItem: LineItem): ProductReturnCard => ({
  lineItem,
  selectedForReturn: false,
  id: uniqueId(),
  returnQuantity: 0,
  returnableQuantity: getReturnableQuantity(lineItem),
})

export const getProductReturnCardFromOrder = (
  order: Order,
  shouldHaveLineItemsOnlyEligibleForReturn?: boolean,
): ProductReturnCard[] => {
  let lineItems = [] as LineItem[]
  order.shipments?.forEach(shipment => {
    if (shipment.lineItems) lineItems.push(...shipment.lineItems)
  })
  if (shouldHaveLineItemsOnlyEligibleForReturn) {
    lineItems = lineItems.filter(isLineItemEligibleForReturn)
  }

  return lineItems.map(makeProductReturnCard)
}

export const getSpidsFromProductReturnCards = (productReturnCards: ProductReturnCard[]): number[] => {
  return productReturnCards.map(item => item.lineItem.productId)
}

const resolveReturnType = (
  productReturnCard: ProductReturnCard,
): Omit<RETURN_TYPES, RETURN_TYPES.REFUND_TO_STORE_CREDIT> | undefined => {
  if (productReturnCard.returnType === RETURN_TYPES.REFUND_TO_STORE_CREDIT) {
    return RETURN_TYPES.REFUND
  }
  if (productReturnCard.returnType === RETURN_TYPES.REFUND) {
    return RETURN_TYPES.REFUND
  }
  return productReturnCard.returnType
}

const resolveRefundMethod = (productReturnCard: ProductReturnCard) => {
  if (productReturnCard.returnType === RETURN_TYPES.REFUND_TO_STORE_CREDIT) {
    return RETURN_MODES.STORE_CREDIT
  }
  if (productReturnCard.returnType === RETURN_TYPES.REFUND) {
    return RETURN_MODES.CARD
  }
  return undefined
}

export const getRefundParameters = (productReturnCard: ProductReturnCard) => ({
  returnType: resolveReturnType(productReturnCard) as string,
  refundMethod: resolveRefundMethod(productReturnCard),
})

export const makeReturnLineItems = (productReturnCard: ProductReturnCard): ReturnLineItem[] => {
  const refundParameters: { returnType: string; refundMethod?: string } = getRefundParameters(productReturnCard)
  const imageUrls = (productReturnCard.photos?.map(pObj => pObj.imageUploadResponse?.url) || []).filter(Boolean)

  return new Array(productReturnCard.returnQuantity).fill(productReturnCard).map(() => ({
    ...refundParameters,
    reasonCode: productReturnCard.reasonCode,
    exchangeProductId:
      productReturnCard.returnType === RETURN_TYPES.EXCHANGE
        ? productReturnCard.exchangeDetails?.exchangeData.exchangeProductId
        : undefined,
    exchangeVariantId:
      productReturnCard.returnType === RETURN_TYPES.EXCHANGE
        ? productReturnCard.exchangeDetails?.exchangeData.exchangeVariantId
        : undefined,
    lineItemId: productReturnCard.lineItem.id,
    returnLineItemReview: {
      subReason: productReturnCard.subReason,
      additionalComments: productReturnCard.additionalComments,
      reason: productReturnCard.reasonCode,
      returnLineItemId: productReturnCard.lineItem.id,
      ...productReturnCard.rating,
    },
    imageUrls: imageUrls,
    restocked: false,
  }))
}

const getProductsWithChildProducts = (products: Product[], spids: number[], selectedSvids: number[]) => {
  const productsBySpids = keyBy(products, 'spid')
  // Fix added fore 100 variants product
  products.forEach(product => {
    product.variants?.forEach(variant => {
      if (!productsBySpids[variant.spid]) {
        productsBySpids[variant.spid] = {
          ...product,
          spid: variant.spid,
        }
      }
    })
  })

  const childSpidsVsParentSpid = products.reduce<Record<string, number>>((childSpidsVsParentSpid, product) => {
    if (product.childSpids) {
      product.childSpids.forEach(childSpid => {
        childSpidsVsParentSpid[childSpid] = product.spid
      })
    }
    return childSpidsVsParentSpid
  }, {})
  return spids
    .map((spid, index) => {
      let parentProduct = productsBySpids[spid]

      if (parentProduct) return parentProduct

      // Case of child product
      const parentProductSpid = childSpidsVsParentSpid[spid]
      parentProduct = productsBySpids[parentProductSpid]
      const selectedSvid = selectedSvids[index]

      const resolveProductIdFromSelectedSvid = (product: Product, selectedSvid: number) =>
        product.variants?.find(productVariant => productVariant.svid === selectedSvid)?.productId

      const childProductId = resolveProductIdFromSelectedSvid(parentProduct, selectedSvid)
      if (!childProductId) return parentProduct

      // Constructing Child Product
      return new Product({
        ...parentProduct,
        spid: spid,
        childSpids: undefined,
        id: childProductId,
        variants: parentProduct.variants?.filter(variant => variant.productId === childProductId),
      })
    })
    .filter(Boolean)
}

export const getProductsFromOrder = async (order: Order, shouldHaveLineItemsOnlyEligibleForReturn: boolean) => {
  const productReturnCards = getProductReturnCardFromOrder(order, shouldHaveLineItemsOnlyEligibleForReturn)

  const selectedVariantIds = productReturnCards.map(item => item.lineItem.variantId)
  const spids = getSpidsFromProductReturnCards(productReturnCards)

  if (!spids.length) return
  const products = await productService.getProducts({
    spids,
    extraParams: {
      isChildProduct: true,
      isRetiredRequired: true,
    },
    fetchFullProduct: true,
  })

  return getProductsWithChildProducts(products, spids, selectedVariantIds)
}

const CATEGORY_RETURN = 'return'

export const trackReturnSuccess = ({ productReturnCards }: { productReturnCards: ProductReturnCard[] }) => {
  const spidVsSvidMap: Record<
    string,
    {
      spid: string
      svid: string
    }
  > = {}
  productReturnCards.forEach(prc => {
    spidVsSvidMap[prc.lineItem.productId] = {
      spid: prc.lineItem.productId?.toString(),
      svid: prc.lineItem.variantId?.toString(),
    }
  })
  const spids = Object.keys(spidVsSvidMap)
  productService
    .getProducts({
      spids: spids,
      useLite: true,
    })
    .then(productsList => {
      const productProperties = productsList.map(prd =>
        getProductPropertiesForTracking(prd, spidVsSvidMap[prd.spid]?.svid),
      )
      trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
        category: CATEGORY_RETURN,
        label: CATEGORY_RETURN,
        action: 'return_successful',
        context: {
          productProperties,
        },
      })
    })
    .catch((error: unknown) => {
      console.error(error)
      throw error
    })
}
